/** Toolbox — full list with descriptions; optional per-tool usage status on Orchestrate. */

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faCircleXmark,
  faMinus,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import type { AvailableTool } from "./types";
import type { ToolOutcome } from "./toolStatus";

interface AgentToolboxProps {
  tools: AvailableTool[];
  /** When set, shows whether each tool was used and whether it succeeded. */
  usage?: Map<string, ToolOutcome>;
  showLegend?: boolean;
}

function statusIcon(outcome: ToolOutcome | undefined, used: boolean) {
  if (!used) {
    return <FontAwesomeIcon icon={faMinus} className="text-[10px] text-slate-600" />;
  }
  if (outcome === "success") {
    return <FontAwesomeIcon icon={faCircleCheck} className="text-[10px] text-emerald-400" />;
  }
  if (outcome === "error") {
    return <FontAwesomeIcon icon={faCircleXmark} className="text-[10px] text-rose-400" />;
  }
  return <FontAwesomeIcon icon={faMinus} className="text-[10px] text-amber-400" />;
}

function statusLabel(outcome: ToolOutcome | undefined, used: boolean): string {
  if (!used) return "Not used";
  if (outcome === "success") return "Used successfully";
  if (outcome === "error") return "Used but failed";
  return "Used — outcome unclear";
}

export function AgentToolbox({ tools, usage, showLegend = false }: AgentToolboxProps) {
  if (tools.length === 0) return null;

  const withUsage = usage != null;

  return (
    <div className="space-y-3">
      {showLegend && withUsage ? (
        <p className="text-xs text-slate-400 leading-relaxed">
          <span className="text-emerald-400">✓</span> used successfully ·{" "}
          <span className="text-rose-400">✗</span> used but failed ·{" "}
          <span className="text-slate-500">—</span> not used this run
        </p>
      ) : (
        <p className="text-xs text-slate-400">
          These are the tools the agent was allowed to use on this task.
        </p>
      )}

      <ul className="space-y-2">
        {tools.map((tool) => {
          const outcome = usage?.get(tool.name);
          const used = usage?.has(tool.name) ?? false;
          return (
            <li
              key={tool.name}
              className="flex items-start gap-3 rounded-lg bg-white/[0.02] px-3 py-2.5 ring-1 ring-white/5"
            >
              {withUsage ? (
                <span className="mt-0.5 w-4 flex-shrink-0" title={statusLabel(outcome, used)}>
                  {statusIcon(outcome, used)}
                </span>
              ) : null}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-200">{tool.friendly}</p>
                {tool.description ? (
                  <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">{tool.description}</p>
                ) : null}
                {withUsage ? (
                  <p className="text-[10px] text-slate-500 mt-1">{statusLabel(outcome, used)}</p>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
